import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton
} from '@mui/material';
import { SportsTennis as TennisIcon, Refresh as RefreshIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getAllUserProfiles, clearGroupTrainingPreference } from '../utils/firebaseUtils';

const DAY_ORDER = ['Pirmadienis', 'Antradienis', 'Trečiadienis', 'Ketvirtadienis', 'Penktadienis', 'Šeštadienis', 'Sekmadienis'];

const formatWantedTimes = (preferences) => {
  if (!preferences?.length) return '—';
  const byDay = {};
  preferences.forEach((p) => {
    const day = p.dayName || p.day || '';
    const time = (p.time || '').trim();
    if (!day || !time) return;
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(time);
  });
  const lines = DAY_ORDER.filter((d) => byDay[d]).map((day) => {
    const times = (byDay[day] || []).sort();
    const timeStr = times.map((t) => `"${t}"`).join('; ');
    return `${day}: ${timeStr}`;
  });
  return lines.length > 0 ? lines.join('\n') : '—';
};

const loadGroupTrainingUsers = async () => {
  const all = await getAllUserProfiles();
  const withPreferences = all.filter(
    (u) =>
      (u.groupTrainingPreferences && u.groupTrainingPreferences.length > 0) ||
      u.groupTrainingSubmittedAt
  );
  withPreferences.sort((a, b) => {
    const ta = a.groupTrainingSubmittedAt?.toMillis?.() ?? 0;
    const tb = b.groupTrainingSubmittedAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
  return withPreferences;
};

const AdminGroupTrainingPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refetch = async () => {
    try {
      const list = await loadGroupTrainingUsers();
      setUsers(list);
    } catch (err) {
      console.error(err);
      setError('Nepavyko įkelti duomenų');
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const list = await loadGroupTrainingUsers();
        if (!cancelled) setUsers(list);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Nepavyko įkelti duomenų');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Refetch when page becomes visible so latest submissions are shown
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete?.userId) return;
    setDeleteConfirmOpen(false);
    setDeleting(true);
    setError('');
    try {
      await clearGroupTrainingPreference(userToDelete.userId);
      await refetch();
    } catch (err) {
      console.error(err);
      setError('Nepavyko pašalinti pageidavimų');
    } finally {
      setDeleting(false);
      setUserToDelete(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TennisIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              Group
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ borderRadius: 2 }}
          >
            Atnaujinti
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Mobile: one horizontal card per user (left: contact, right: times) */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {users.map((user) => (
            <Paper
              key={user.id}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 0,
                mb: 2,
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ flex: 1, p: 1.5, borderRight: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{user.fullName || '—'}</Typography>
                <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                  {user.email ? (
                    <Link href={`mailto:${user.email}`} sx={{ fontSize: 'inherit' }}>{user.email}</Link>
                  ) : (
                    '—'
                  )}
                </Typography>
                <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                  {user.phoneNumber ? (
                    <Link href={`tel:${user.phoneNumber}`} sx={{ fontSize: 'inherit' }}>{user.phoneNumber}</Link>
                  ) : (
                    '—'
                  )}
                </Typography>
                <Typography variant="body2">NTRP: {user.ntrpLevel ? <Chip label={user.ntrpLevel} size="small" color="primary" variant="outlined" sx={{ height: 20 }} /> : '—'}</Typography>
                <Typography variant="body2">Kartų/sav.: {user.trainingsPerWeek ?? '—'}</Typography>
              </Box>
              <Box sx={{ flex: 1, p: 1.5, minWidth: 0, bgcolor: 'grey.50', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-line', display: 'block', fontSize: '0.8rem' }}>
                  {formatWantedTimes(user.groupTrainingPreferences)}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(user)}
                    disabled={deleting}
                    sx={{ p: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Desktop: table */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            display: { xs: 'none', md: 'block' }
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 600 }}>Kontaktinė informacija</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Pageidaujami laikai ir dienos</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 80 }}>Veiksmai</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {user.fullName || '—'}
                      </Typography>
                      <Typography variant="body2">
                        {user.email ? (
                          <Link href={`mailto:${user.email}`} sx={{ fontSize: 'inherit' }}>
                            {user.email}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </Typography>
                      <Typography variant="body2">
                        {user.phoneNumber ? (
                          <Link href={`tel:${user.phoneNumber}`} sx={{ fontSize: 'inherit' }}>
                            {user.phoneNumber}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </Typography>
                      <Box>
                        {user.ntrpLevel ? (
                          <Chip label={user.ntrpLevel} size="small" color="primary" variant="outlined" />
                        ) : (
                          <Typography variant="body2" component="span">—</Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 400, whiteSpace: 'pre-line', verticalAlign: 'top' }}>
                    <Typography variant="body2" component="span">
                      {formatWantedTimes(user.groupTrainingPreferences)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(user)}
                      disabled={deleting}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Pašalinti pageidavimus?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Ar tikrai norite pašalinti {userToDelete?.fullName || 'vartotojo'} grupinės treniruotės pageidavimus? Šis veiksmas negrįžtamas.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ borderRadius: 2 }}>
              Atšaukti
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              color="error"
              disabled={deleting}
              sx={{ borderRadius: 2 }}
            >
              {deleting ? 'Pašalinama...' : 'Pašalinti'}
            </Button>
          </DialogActions>
        </Dialog>

        {users.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Dar nėra vartotojų, pateikusių pageidavimus grupinėms treniruotėms.
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default AdminGroupTrainingPage;
